import React, { useState } from "react";
import { Eye, MapPin, Phone, Mail, AlertTriangle, KeyRound, ShieldOff, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { brl, formatUSPhone } from "../../lib/format";
import { languageLabel } from "../../lib/languages";
import { fileUrl, api, formatApiError } from "../../lib/api";
import { MapView } from "../MapView";
import { ContactActions } from "../ContactActions";
import { HouseDetailsView } from "./HouseDetailsView";
import { useConfirm } from "../../hooks/useConfirm";

function formatFullAddress(c) {
  if (!c) return "";
  const line1 = [c.address, c.apt_number ? `Apt ${c.apt_number}` : ""]
    .filter(Boolean)
    .join(" ");
  const line2 = [c.city, c.state, c.zip_code].filter(Boolean).join(", ");
  return [line1, line2].filter(Boolean).join(" — ");
}

function Info({ label, icon, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1 mb-0.5">
        {icon}
        {label}
      </div>
      <div className="text-sm text-slate-800">{children}</div>
    </div>
  );
}

function PortalAccessSection({ client, onUpdate }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const confirm = useConfirm();
  const enabled = !!client?.portal_enabled;
  const hasEmail = !!client?.email;

  async function enable() {
    if (password.length < 6) {
      toast.error("Senha deve ter pelo menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/portal/enable`, { password });
      const base = enabled ? "Senha do portal redefinida" : "Portal habilitado para o cliente";
      toast.success(
        data?.email_sent ? `${base} — credenciais enviadas por e-mail` : base,
      );
      setPassword("");
      onUpdate && onUpdate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  async function disable() {
    const ok = await confirm({
      title: "Desativar portal?",
      description: "O cliente não conseguirá mais acessar a área restrita.",
      confirmText: "Desativar",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.post(`/clients/${client.id}/portal/disable`);
      toast.success("Portal desativado");
      onUpdate && onUpdate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div className="mt-5 p-4 rounded-xl border border-slate-200 bg-white" data-testid="client-portal-access">
      <div className="flex items-center gap-2 mb-2">
        <KeyRound size={16} className="text-blue-700" />
        <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
          Acesso ao Portal do Cliente
        </div>
        {enabled && (
          <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
            Ativo
          </span>
        )}
      </div>
      {!hasEmail ? (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">
          ⚠ Cadastre um e-mail no cliente para liberar o acesso ao portal.
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block">
              <span className="block text-xs text-slate-600 mb-1">
                {enabled ? "Nova senha (redefinir)" : "Definir senha de acesso"}
              </span>
              <input
                data-testid="client-portal-password"
                type="password"
                className="cm-input"
                placeholder="Mín. 6 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
          </div>
          <Button
            data-testid="client-portal-enable-btn"
            onClick={enable}
            disabled={loading || password.length < 6}
          >
            {loading ? "Salvando…" : enabled ? "Redefinir senha" : "Habilitar portal"}
          </Button>
          {enabled && (
            <Button
              data-testid="client-portal-disable-btn"
              variant="outline"
              onClick={disable}
              className="text-rose-700 border-rose-200 hover:bg-rose-50"
            >
              <ShieldOff size={14} className="mr-1" /> Desativar
            </Button>
          )}
        </div>
      )}
      {enabled && (
        <div className="mt-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50">
          <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold mb-1.5">
            URL de acesso do cliente
          </div>
          <div className="flex items-center gap-2">
            <code
              data-testid="client-portal-url"
              className="flex-1 text-xs font-mono bg-white border border-emerald-200 rounded px-2 py-1.5 truncate text-slate-800"
            >
              {window.location.origin}/portal/login
            </code>
            <button
              type="button"
              data-testid="client-portal-copy-url"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/portal/login`);
                toast.success("URL copiada");
              }}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
              title="Copiar URL"
            >
              <Copy size={12} /> Copiar
            </button>
          </div>
          <div className="text-[11px] text-emerald-800 mt-2">
            Login: <strong className="font-mono">{client.email}</strong>
            <span className="text-emerald-700/70"> · use a senha definida acima</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function ClientViewDialog({ client, onClose }) {
  if (!client) return null;
  const fullAddress = formatFullAddress(client);
  return (
    <Dialog open={!!client} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="client-view-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye size={18} /> {client.name}
          </DialogTitle>
        </DialogHeader>

        {client.observations && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-start gap-2 mb-3">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold mb-1">Observações Importantes</div>
              <div className="whitespace-pre-line">{client.observations}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <Info label="Endereço Completo" icon={<MapPin size={14} />}>
              {fullAddress || client.address || "—"}
            </Info>
            {client.door_code && (
              <Info label="Código da Porta">
                <span className="font-mono font-bold text-lg">
                  🔑 {client.door_code}
                </span>
              </Info>
            )}
            <Info label="Telefone" icon={<Phone size={14} />}>
              {client.phone ? `+1 ${formatUSPhone(client.phone)}` : "—"}
            </Info>
            {client.email && (
              <Info label="E-mail" icon={<Mail size={14} />}>
                {client.email}
              </Info>
            )}
            <Info label="Tipo de Imóvel">{client.property_type || "—"}</Info>
            <Info label="Frequência">{client.frequency || "—"}</Info>
            <Info label="Idioma Falado">
              {client.language ? languageLabel(client.language) : "—"}
            </Info>
            <Info label="Valor da Limpeza">
              <strong className="text-lg text-emerald-700">
                {brl(client.value)}
              </strong>
            </Info>

            {(client.has_whatsapp ||
              client.has_telegram ||
              client.has_instagram ||
              client.has_facebook) && (
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
                  Canais de Contato
                </div>
                <ContactActions
                  client={client}
                  size="lg"
                  testIdPrefix={`client-view-${client.id}-contact`}
                />
              </div>
            )}
          </div>

          <div className="space-y-3">
            {client.lat && client.lng && (
              <MapView
                lat={client.lat}
                lng={client.lng}
                popup={fullAddress}
                height={240}
              />
            )}
          </div>
        </div>

        {client.cleaning_checklist && client.cleaning_checklist.length > 0 && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
              📝 Checklist de Limpeza ({client.cleaning_checklist.length} itens)
            </div>
            <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-slate-50">
              {client.cleaning_checklist.map((it, i) => (
                <li
                  key={`${i}-${it}`}
                  className="flex items-center gap-2 px-3 py-2 text-sm"
                >
                  <span className="text-slate-400 text-xs w-6">{i + 1}.</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {client.photos && client.photos.length > 0 && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
              Fotos do Local ({client.photos.length})
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
              {client.photos.map((p) => (
                <a
                  key={p}
                  href={fileUrl(p)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0 w-[520px] max-w-[90vw] group relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50 snap-start"
                >
                  <img
                    src={fileUrl(p)}
                    alt=""
                    className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </a>
              ))}
            </div>
          </div>
        )}

        <HouseDetailsView client={client} />

        <PortalAccessSection client={client} onUpdate={onClose} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
